<!-- Localized -->
<template>
  <UISplitViewModal :config="modalConfig">
    <template v-slot:[detailsSlots.title]>{{ $t('case_creation.risk_score_details') }}</template>
    <template v-slot:[detailsSlots.content]>
      <neo-layout :loading="loading">

        <div class="flex flex-col w-full gap-6">
          <div class="risk-grid grid auto-rows-fr px-4 pr-6 py-1 bg-base-400 rounded-lg font-medium w-full">
              <p>{{ overalRiskLabel.label_text }}</p>
              <p 
                :style="{ color: overalRiskLabel.label?.label_color }" 
                :title="overalRiskLabel.label?.label_name"
              >
                {{ overalRiskLabel.label?.label_name }}
              </p>

              <div class="flex items-center justify-center" v-if="getRiskScoreData?.label_type !== 'workflow' && !getRiskScoreData?.is_case_default && userPermissions.updateRiskLabel">
                  <font-awesome-icon
                    :class="{
                      'cursor-pointer text-primary hover:primary-300': userPermissions.updateRiskLabel,
                      'text-gray-500 pointer-events-none': !userPermissions.updateRiskLabel,
                    }"
                    icon="undo"
                    @click="confirmDeleteManualRiskLabel"
                  />
              </div>
          </div>

          <div class="w-full flex flex-col items-stretch gap-2">
              <div class="risk-grid grid grid-cols-4 bg-thead rounded-lg px-4 pr-6 py-1 font-medium">
                <p>{{ $t('case_creation.entity_name') }}</p>
                <p>
                  <template v-if="isScoreBased">
                    {{ $t('case_creation.risk') }}
                  </template>
                </p>
                <p>{{ $t('case_creation.score') }}</p>
              </div>
          
              <div
                v-for="entity in entities"
                :key="entity.id"
              >
                <div
                  class="risk-grid grid grid-cols-4 items-center bg-base-250 px-4 pr-6 py-1 font-medium cursor-pointer"
                  :class="{
                    'rounded-t-lg': riskCategoriesView[entity.id],
                    'rounded-lg': !riskCategoriesView[entity.id],
                  }"
                  @click="showRiskCategories(entity.id)"
                >
                  <p>{{ entity.name }}</p>
                  <p :style="{ color: entity.label_color }">
                    <template v-if="isScoreBased">
                      {{ entity.label_name }}
                    </template>
                  </p>
                  <p>{{ entity.score }}</p>
                  <font-awesome-icon
                    icon="chevron-down"
                    class="ml-auto text-main text-sm"
                    :class="{ 'rotate': riskCategoriesView[entity.id] }"
                  />
                </div>

                <div
                  v-if="riskCategoriesView[entity.id]"
                  class="p-2 border border-solid border-gray-100 rounded-b-lg flex flex-col items-stretch gap-1 w-full"
                >
                  <div
                    v-for="category in entity.categories"
                    :key="`${entity.id}-${category.id}`"
                    class="risk-grid grid grid-cols-3 auto-rows-fr items-center bg-gray-100 rounded-lg px-4 py-1 cursor-pointer"
                    @click="showCategoryBreakdown(category.id, category)"
                  >
                    <p class="flex items-center gap-2">
                      <span
                        :style="{ backgroundColor: category.color }"
                        class="w-4 h-4 rounded-full flex-shrink-0"
                      />
                      <span>{{ category.name }}</span>
                    </p>
                    <p :style="{ color: category.label_color }">
                      <template v-if="isScoreBased">
                        {{ category.label_name }}
                      </template>
                    </p>
                    <p>{{ category.score }}</p>
                    <template v-if="category.assessments.length > 0">
                      <font-awesome-icon
                        v-if="category.id === selectedCategoryId"
                        icon="chevron-left"
                        class="ml-auto text-main text-sm"
                      />
                      <font-awesome-icon
                        v-else
                        icon="chevron-right"
                        class="ml-auto text-main text-sm"
                      />
                    </template>
                  </div>
                </div>
            </div>
          
            <div class="risk-grid grid auto-rows-fr px-4 pr-6 py-1 bg-base-400 rounded-lg font-medium">
                <p>{{ `${$t('case_creation.overall_risk_score')}` }}</p>
                <p 
                  :style="{ color: getRiskScoreData?.label_color }" 
                  :title="getRiskScoreData?.label_name"
                >
                  <template v-if="isScoreBased">
                    {{ getRiskScoreData?.label_name }}
                  </template>
                </p>
                <p>{{ overallRiskScore }}</p>
            </div>
          </div>
        </div>

        <div v-if="!isRiskCategoryBreakdownShown" class="flex justify-end mt-4">
          <Button
            :text="$t('case_creation.ok')"
            type="primary"
            @click="closeModal"
          />
        </div>

        <ModalConfirm
          ref="confirm-modal"
        />
      </neo-layout>
    </template>

    <template v-slot:[breakdownSlots.title]>{{ $t('case_creation.entitys_risk_category_score_details') }}</template>

    <template v-slot:[breakdownSlots.content]>
        <div class="flex flex-col w-full">
          <div class="breakdown-grid grid  items-center bg-base-250 px-4 pr-6 py-1 rounded-t-lg font-medium">
            <p>{{ categoryBreakdownName }}</p>
            <p>{{ categoryBreakdownScore }}</p>
          </div>

          <div class="p-2 border border-solid border-gray-100 rounded-b-lg flex flex-col items-stretch gap-1 w-full">
            <div class="breakdown-grid grid  auto-rows-fr items-center bg-gray-300 rounded-lg px-4 py-1 text-sm">
              <p>{{ $t('case_creation.risk_assessment_name') }}</p>
              <p>{{ $t('case_creation.score') }}</p>
            </div>
            <div
              v-for="assessment in categoryBreakdownAssessments"
              :key="assessment.id"
              class="breakdown-grid grid auto-rows-fr items-center bg-gray-100 rounded-lg px-4 py-1"
            >
              <p>{{ assessment.name }}</p>
              <p>{{ assessment.score }}</p>
            </div>
          </div>
          <div class="flex justify-end mt-auto">
            <Button
              :text="$t('case_creation.ok')"
              type="primary"
              @click="resetRiskCategory"
            />
          </div>
        </div>
    </template>
  </UISplitViewModal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Button from "@/components/button";
import ModalConfirm from "@shared/modal-confirm";
import NeoLayout from "@shared/components/neo-layout";
import {
  checkPermission,
} from "@shared/utils/functions";
import {
  RiskScoreLabelTypes,
  labelSourceTypeMap,
  riskScoreBasedValueMap,
} from "./utils/constants"

import { caseEntityTypesMap } from "@shared/modules/localization/utils/caseEntityTranslation/constants";
import { translateEntity } from "@shared/modules/localization/utils/caseEntityTranslation";

export default {
  name: "CaseScoreBreakdown",

  components: {
    Button,
    ModalConfirm,
    NeoLayout
  },

  props: {
    caseTypeEntityId: {
      type: String,
      required: true,
    },
    caseEntities: {
      type: Array,
      required: true,
    },
  },

    data() {
      return {
        RiskScoreLabelTypes,
        loading: false,
        riskCategoriesView: {},
        riskCategoryBreakdownView: {},
        selectedCategoryId: null,
        isRiskCategoryBreakdownShown: false,
      };
    },

    computed: {
      ...mapGetters({
        getRiskScoreData: "CaseCreationStore/getRiskScoreData",
        isRiskScoreModalShown: "CaseCreationStore/getIsRiskScoreModalShown",
      }),

      userPermissions() {
        return {
          updateRiskLabel: checkPermission('caseRiskScore.update_case_risk_label')
        }
      },

      sourceTypeRiskLabel() {
        return {
          [labelSourceTypeMap.RISK_SCORE]: this.$t('case_creation.score_based'),
          [labelSourceTypeMap.WORKFLOW]: this.$t('case_creation.trigger_based'),
        }
      },

      overalRiskLabel() {
        return this.overalRiskLabels[this.getRiskScoreData?.label_type] ?? {};
      },

      overalRiskLabels() {
        const {
          label_type,
          label_id,
          label_color,
          label_name,
          computed_label_id,
          computed_label_color,
          computed_label_name,
          label_source_type,
          is_case_default,
        } = this.getRiskScoreData;

        // current manual/workflow or another manual label OR automatically calculated label in the case we DON'T HAVE separate "computed" label:
        const label = { 
          label_id,
          label_color,
          label_name
        }

        // automatically calculated label in the case we HAVE a separate "computed" label
        const computed = {
          label_id: computed_label_id,
          label_color: computed_label_color,
          label_name: computed_label_name,
        }

        // if there is a separate "computed" label then we additionally have either "manual" or "workflow" label
        const isSeparateComputedLabel = !!computed_label_id;

        // temporary utility:
        const defaultLabelType = isSeparateComputedLabel
          ? RiskScoreLabelTypes.MANUAL
          : RiskScoreLabelTypes.COMPUTED;
        
        const nonManualLabelInfo = {
          label: isSeparateComputedLabel ? computed : label,
          label_text: is_case_default
            ? this.$t('case_creation.overall_risk')
            : this.riskLabelsText[label_source_type || defaultLabelType]
        };

        return {
          [RiskScoreLabelTypes.COMPUTED]: nonManualLabelInfo,
          [RiskScoreLabelTypes.WORKFLOW]: nonManualLabelInfo,
          [RiskScoreLabelTypes.MANUAL]: this.isManual ? {
            label,
            label_type: label_type || defaultLabelType,
            label_text: is_case_default
              ? this.$t('case_creation.overall_risk')
              : this.riskLabelsText[label_type || defaultLabelType]
          } : null,
        }
      },

      riskLabelsText() {
        return {
          [RiskScoreLabelTypes.WORKFLOW]: `${this.$t('case_creation.overall_risk')} (${this.$t('case_creation.trigger_based')})`,
          [RiskScoreLabelTypes.MANUAL]: `${this.$t('case_creation.overall_risk')} (${this.$t('case_creation.manual_override')})`,
          [RiskScoreLabelTypes.COMPUTED]: `${this.$t('case_creation.overall_risk')} (${this.$t('case_creation.score_based')})`,
          [RiskScoreLabelTypes.RISK_SCORE]: `${this.$t('case_creation.overall_risk')} (${this.$t('case_creation.score_based')})`,
        };
      },

      isScoreBased() {
        return !!riskScoreBasedValueMap[this.getRiskScoreData?.label_source_type];
      },

      isManual() {
        return this.getRiskScoreData?.label_type === labelSourceTypeMap.MANUAL;
      },

      caseEntity() {
        return this.caseEntities?.find(ce => ce.id === this.caseTypeEntityId) ?? null;
      },

      entities() {
        return (this.getRiskScoreData?.entities || [])
          .filter(ce => ce.id !== this.caseTypeEntityId).map(entity => {
            const _entity = this.translateEntity(entity);
            return {
                ..._entity,
                categories: _entity.categories.map(category => {
                  const _category = this.translateEntity(category)
                  return {
                    ..._category,
                    assessments: _category.assessments.map(assessment => this.translateEntity(assessment)),
                  }
                })
            }
          });
      },

      overallRiskScore() {
        return this.getRiskScoreData?.score ?? 0;
      },

      categoryBreakdownName() {
        return this.riskCategoryBreakdownView[this.selectedCategoryId]?.name;
      },

      categoryBreakdownScore() {
        return this.riskCategoryBreakdownView[this.selectedCategoryId]?.score;
      },

      categoryBreakdownAssessments() {
        return this.riskCategoryBreakdownView[this.selectedCategoryId]?.assessments;
      },

      modalConfig() {
        return [
          {
            name: "details",
            show: this.isRiskScoreModalShown,
            onClose: this.closeModal,
          },
          {
            name: "breakdown",
            show: this.isRiskCategoryBreakdownShown,
            onClose: this.resetRiskCategory,
          }
        ];
      },

      detailsSlots() {
        const [ details ] = this.modalConfig;
        return {
          title: `${details.name}:title`,
          content: `${details.name}:content`,
        };
      },

      breakdownSlots() {
        const [ , breakdown ] = this.modalConfig;
        return {
          title: `${breakdown.name}:title`,
          content: `${breakdown.name}:content`,
        };
      },
    },

    methods: {
      ...mapActions({
        deleteRiskLabel: "CaseCreationStore/deleteRiskLabel",
        fetchRiskScore: "CaseCreationStore/fetchRiskScore",
        toggleRiskScoreModal: "CaseCreationStore/toggleRiskScoreModal",
      }),

      checkPermission,

      translateEntity(entity) {
        return translateEntity(entity, caseEntityTypesMap.RISK);
      },

      async confirmDeleteManualRiskLabel() {
        if (!this.userPermissions.updateRiskLabel) return;

        const confirm = await this.$refs['confirm-modal'].show({
            message: this.$t('case_creation.confirm_delete_manual_risk_label')
        });
        
        if (confirm) {
          this.deleteManualRiskLabel();
        }
      }, 

      async deleteManualRiskLabel() {
        try {
          this.loading = true;
          await this.deleteRiskLabel({ 
            case_id: this.$route.query.caseid, 
            label_id: this.getRiskScoreData?.computed_label_id
          })

          await this.fetchRiskScore({ 
            case_id: this.$route.query.caseid
          })
          this.$toast.success(this.$t('case_creation.success_delete_manual_risk_label'));
        } catch(e) {
          this.$toast.error(this.$t('case_creation.error_delete_manual_risk_label'));
        } finally {
          this.loading = false;
        }
      },
  
      showRiskCategories(entity_id) {
        this.$set(this.riskCategoriesView, entity_id, !this.riskCategoriesView[entity_id]);
      },

      showCategoryBreakdown(category_id, category) {
        if (!category.assessments.length) return;
        if (category_id === this.selectedCategoryId) {
          return this.resetRiskCategory();
        }

        this.isRiskCategoryBreakdownShown = true;
        this.selectedCategoryId = category_id;
        this.$set(this.riskCategoryBreakdownView, category_id, category);
      },

      closeModal() {
        this.toggleRiskScoreModal();
        this.riskCategoriesView = {};
        this.resetRiskCategory();
      },

      resetRiskCategory() {
        this.selectedRiskCategory = {};
        this.isRiskCategoryBreakdownShown = false;
        this.selectedCategoryId = null;
      },
    }
}
</script>

<style lang="scss" scoped>
.rotate {
  transform: rotate(180deg);
}

.risk-grid {
  grid-template-columns: 1fr 100px 100px 25px;

  & > * {
    &:not(:last-child) {
      @apply pr-1;
    }
    &:not(:first-child) {
      @apply pl-1;
    }
  }
}

.breakdown-grid {
  grid-template-columns: 1fr 75px;
}

.risk-grid,
.breakdown-grid {
    p, span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
</style>
